/**
 * General MIDI percussion (channel 10) → an audible drum kit.
 *
 * WHY THIS EXISTS
 * ---------------
 * A MIDI drum track carries pitches that are NOT pitches: on GM channel 10,
 * note 36 means "kick", 38 means "snare", 42 means "closed hi-hat". Every
 * renderer that treats those numbers as notes and hands them to a melodic
 * instrument produces a low piano mush instead of a groove. /midi did exactly
 * that until 2026-08-16 — it even printed "standard kit" in its own instrument
 * list and then played the track on Acoustic Grand Piano.
 *
 * The samples are the CC0 (VCSL) set already shipped for /virtual-drums —
 * public/data/virtual-drums/. Nothing new is downloaded for the site; a drum
 * track typically needs 3-5 pieces (~150 KB) against ~2.4 MB for ONE melodic
 * FluidR3 instrument.
 *
 * The synth voices are the same fallback bank /virtual-drums uses, so a
 * missing/blocked sample pack degrades to something percussive rather than to
 * silence — or worse, back to piano.
 *
 * BOTH LANES, ONE KIT
 * -------------------
 * Callers get plain AudioBuffers, which suits both consumers:
 *   • live playback  — wrap in a BufferSource on the live AudioContext;
 *   • offline render — read getChannelData() and mix into the output array.
 * That is deliberate: /midi's two lanes drifting apart is the class of bug
 * this module exists to end, so neither lane gets its own notion of a drum.
 *
 * API
 *   GmDrumKit.pieceForNote(midi)        → piece id | null   (DrumMidiMap.gm — the shared table)
 *   GmDrumKit.load(ctx, midiNotes)      → Promise<{ [midi]: {buffer, gain, pan} }>
 */
(function (global) {
    'use strict';

    /* ── which note plays which drum ──────────────────────────────────────────
       ONE table for every drum tool: DrumMidiMap (public/js/virtual-drums/
       drum-midi-map.js), read here through its General MIDI FILE view, gm(). This
       module kept its own copy until 30.10.2026 and it had drifted: no high tom at
       all (48 and 50 played the middle tom, 45 the floor tom) while /virtual-drums
       had grown three. Load drum-midi-map.js before this file (MidiInputHasOneOwnerTest). */
    function noteMap() { return global.DrumMidiMap && typeof global.DrumMidiMap.gm === 'function' ? global.DrumMidiMap : null; }
    /* Which pack holds each piece. Kit pieces live in `acoustic`, hand
       percussion in `percussion` — same split as the manifest. */
    var PIECE_PACK = {
        kick: 'acoustic', snare: 'acoustic', hatClosed: 'acoustic', hatOpen: 'acoustic',
        hatPedal: 'acoustic', tom3: 'acoustic', tom1: 'acoustic', tom2: 'acoustic', crash: 'acoustic', ride: 'acoustic',
        tambourine: 'percussion', cowbell: 'percussion', shaker: 'percussion', claves: 'percussion',
        woodblock: 'percussion', bongoHi: 'percussion', bongoLo: 'percussion', congaHi: 'percussion',
        congaLo: 'percussion', agogoHi: 'percussion', agogoLo: 'percussion',
        darbuka: 'percussion', darbukaTek: 'percussion'
    };

    /* Recorded levels differ per piece — same table /virtual-drums mixes with. */
    var PIECE_GAIN = {
        kick: 1.0, snare: 0.9, hatClosed: 0.7, hatOpen: 0.7, hatPedal: 0.6,
        tom3: 0.95, tom1: 0.95, tom2: 0.95, crash: 0.8, ride: 0.75,
        bongoHi: 0.9, bongoLo: 0.95, congaHi: 0.95, congaLo: 1.0,
        darbuka: 0.95, darbukaTek: 0.85, tambourine: 0.8, cowbell: 0.8,
        claves: 0.8, shaker: 0.7, agogoHi: 0.8, agogoLo: 0.8, woodblock: 0.85
    };

    /* Overheads spread so a rendered kit is not a mono point source. */
    var PIECE_PAN = {
        hatClosed: -0.35, hatOpen: -0.35, hatPedal: -0.35, tom3: -0.34, tom1: -0.18, tom2: 0.28,
        crash: -0.42, ride: 0.42, bongoHi: -0.2, bongoLo: -0.05, congaHi: 0.12,
        congaLo: 0.28, darbuka: -0.1, darbukaTek: 0.1, tambourine: -0.25,
        cowbell: 0.2, claves: 0.18, shaker: -0.15, agogoHi: -0.12, agogoLo: 0.06,
        woodblock: 0.15
    };

    /* ── synth fallback bank ──────────────────────────────────────────────────
       Ported from /virtual-drums so a blocked sample pack still sounds like a
       drum. Pure functions of (ctx) → AudioBuffer; they work unchanged in an
       OfflineAudioContext, which is what the export lane renders into. */
    function mkBuf(ctx, dur, fn, post) {
        var sr = ctx.sampleRate, len = Math.max(1, Math.ceil(sr * dur));
        var b = ctx.createBuffer(1, len, sr), d = b.getChannelData(0);
        for (var i = 0; i < len; i++) d[i] = fn(i / sr, i);
        if (post) post(d);
        return b;
    }
    function noiseHP(d) { for (var i = d.length - 1; i > 0; i--) d[i] = d[i] - d[i - 1] * 0.7; return d; }

    var SYNTH = {
        kick: function (c) { return mkBuf(c, 0.26, function (t) { var click = Math.sin(2 * Math.PI * 1200 * t) * Math.exp(-t * 120) * 0.3; return (Math.sin(2 * Math.PI * (135 * Math.exp(-t * 16) + 48) * t) * Math.exp(-t * 8) + click) * 0.85; }); },
        snare: function (c) { return mkBuf(c, 0.2, function (t) { var tone = (Math.sin(2 * Math.PI * 190 * t) + Math.sin(2 * Math.PI * 278 * t)) * 0.3; var n = (Math.random() * 2 - 1) * 0.7; return (tone + n) * Math.exp(-t * 20) * 0.7; }); },
        hatClosed: function (c) { return mkBuf(c, 0.06, function (t) { return (Math.random() * 2 - 1) * Math.exp(-t * 60) * 0.4; }, noiseHP); },
        hatOpen: function (c) { return mkBuf(c, 0.3, function (t) { return (Math.random() * 2 - 1) * Math.exp(-t * 9) * 0.34; }); },
        hatPedal: function (c) { return mkBuf(c, 0.05, function (t) { return (Math.random() * 2 - 1) * Math.exp(-t * 70) * 0.3; }); },
        tom3: function (c) { return mkBuf(c, 0.233, function (t) { return Math.sin(2 * Math.PI * (229 * Math.exp(-t * 12.03) + 108.3) * t) * Math.exp(-t * 9.63) * 0.8; }); },
        tom1: function (c) { return mkBuf(c, 0.28, function (t) { return Math.sin(2 * Math.PI * (190 * Math.exp(-t * 10) + 90) * t) * Math.exp(-t * 8) * 0.8; }); },
        tom2: function (c) { return mkBuf(c, 0.34, function (t) { return Math.sin(2 * Math.PI * (130 * Math.exp(-t * 9) + 62) * t) * Math.exp(-t * 7) * 0.8; }); },
        crash: function (c) { return mkBuf(c, 1.2, function (t) { return (Math.random() * 2 - 1) * Math.exp(-t * 3.2) * 0.4; }, noiseHP); },
        ride: function (c) { return mkBuf(c, 1.0, function (t) { var ping = Math.sin(2 * Math.PI * 520 * t) * Math.exp(-t * 7) * 0.25; return ping + (Math.random() * 2 - 1) * Math.exp(-t * 4) * 0.18; }, noiseHP); },
        bongoHi: function (c) { return mkBuf(c, 0.14, function (t) { return Math.sin(2 * Math.PI * (320 * Math.exp(-t * 16) + 150) * t) * Math.exp(-t * 17) * 0.7; }); },
        bongoLo: function (c) { return mkBuf(c, 0.16, function (t) { return Math.sin(2 * Math.PI * (230 * Math.exp(-t * 14) + 110) * t) * Math.exp(-t * 15) * 0.72; }); },
        congaHi: function (c) { return mkBuf(c, 0.2, function (t) { return Math.sin(2 * Math.PI * (260 * Math.exp(-t * 12) + 120) * t) * Math.exp(-t * 12) * 0.75; }); },
        congaLo: function (c) { return mkBuf(c, 0.24, function (t) { return Math.sin(2 * Math.PI * (180 * Math.exp(-t * 10) + 85) * t) * Math.exp(-t * 10) * 0.78; }); },
        darbuka: function (c) { return mkBuf(c, 0.18, function (t) { return Math.sin(2 * Math.PI * (150 * Math.exp(-t * 18) + 75) * t) * Math.exp(-t * 16) * 0.75; }); },
        darbukaTek: function (c) { return mkBuf(c, 0.09, function (t) { return ((Math.random() * 2 - 1) * 0.5 + Math.sin(2 * Math.PI * 600 * t) * 0.5) * Math.exp(-t * 45) * 0.55; }); },
        tambourine: function (c) { return mkBuf(c, 0.18, function (t) { var n = Math.random() * 2 - 1; return n * Math.exp(-t * 22) * 0.32 + (Math.sin(2 * Math.PI * 3200 * t) + Math.sin(2 * Math.PI * 5100 * t)) * 0.07 * Math.exp(-t * 35); }, noiseHP); },
        cowbell: function (c) { return mkBuf(c, 0.18, function (t) { return (Math.sin(2 * Math.PI * 560 * t) * 0.6 + Math.sin(2 * Math.PI * 845 * t) * 0.4) * Math.exp(-t * 12) * 0.55; }); },
        claves: function (c) { return mkBuf(c, 0.05, function (t) { return Math.sin(2 * Math.PI * 2500 * t) * Math.exp(-t * 80) * 0.7; }); },
        shaker: function (c) { return mkBuf(c, 0.1, function (t) { return (Math.random() * 2 - 1) * Math.exp(-t * 30) * 0.32; }, noiseHP); },
        agogoHi: function (c) { return mkBuf(c, 0.22, function (t) { return (Math.sin(2 * Math.PI * 1050 * t) * 0.6 + Math.sin(2 * Math.PI * 2100 * t) * 0.3) * Math.exp(-t * 11) * 0.5; }); },
        agogoLo: function (c) { return mkBuf(c, 0.24, function (t) { return (Math.sin(2 * Math.PI * 780 * t) * 0.6 + Math.sin(2 * Math.PI * 1560 * t) * 0.3) * Math.exp(-t * 11) * 0.5; }); },
        woodblock: function (c) { return mkBuf(c, 0.045, function (t) { return Math.sin(2 * Math.PI * 900 * t) * Math.exp(-t * 75) * 0.7; }); }
    };

    var BASE = '/data/virtual-drums';
    var FMT = null;              // resolved once from what the browser can play
    var manifestPromise = null;

    function pickFormat() {
        if (FMT) return FMT;
        var a = document.createElement('audio');
        FMT = (a.canPlayType && a.canPlayType('audio/ogg; codecs="vorbis"')) ? 'ogg' : 'mp3';
        return FMT;
    }

    function getManifest() {
        if (!manifestPromise) {
            manifestPromise = fetch(BASE + '/manifest.json')
                .then(function (r) { if (!r.ok) throw new Error('manifest ' + r.status); return r.json(); })
                .catch(function () { return null; });   // → synth bank
        }
        return manifestPromise;
    }

    /* decodeAudioData DETACHES its input — always decode a copy, or a second
       consumer of the same bytes gets a zero-length buffer. */
    function decode(ctx, url) {
        return fetch(url).then(function (r) {
            if (!r.ok) throw new Error(url + ' ' + r.status);
            return r.arrayBuffer();
        }).then(function (ab) {
            return new Promise(function (res, rej) {
                var p = ctx.decodeAudioData(ab.slice(0), res, rej);
                if (p && p.then) p.then(res, rej);
            });
        });
    }

    function pieceForNote(midi) {
        var M = noteMap();
        return M ? M.gm(midi) : null;
    }

    /**
     * Velocity layer + round-robin, the same selection /virtual-drums makes.
     *
     * ⚠ Layer 0 is the SOFTEST hit, not a "default" one. Loading only layer 0
     * and scaling it by gain sounds wrong in a way that is easy to ship and hard
     * to notice: a soft hi-hat is dull as well as quiet, so a busy hat pattern
     * loses its entire high end. Measured on a kick/snare/hat loop — the >4 kHz
     * share came out BELOW the piano it was replacing.
     */
    function pickLayer(entry, velocity, seq) {
        var layers = entry.layers;
        if (!layers || !layers.length) return null;
        var v = (typeof velocity === 'number') ? Math.max(0, Math.min(1, velocity)) : 0.85;
        var li = Math.min(layers.length - 1, Math.max(0, Math.floor(v * layers.length)));
        var rr = layers[li];
        if (!rr || !rr.length) return null;
        return rr[(seq || 0) % rr.length] || rr[0];
    }

    /**
     * @param {BaseAudioContext} ctx    live or offline — buffers are portable
     * @param {number[]} midiNotes      the percussion notes the file actually plays
     * @returns {Promise<Object>}       midi note → {layers, gain, pan, piece}
     */
    function load(ctx, midiNotes) {
        var pieces = {};
        (midiNotes || []).forEach(function (m) {
            var p = pieceForNote(m);
            if (p) pieces[p] = true;
        });
        var wanted = Object.keys(pieces);
        if (!wanted.length) return Promise.resolve({});

        return getManifest().then(function (man) {
            var fmt = pickFormat();
            var byPiece = {};
            var sampled = {};            // pieces that got the recorded samples (not the synth fallback)

            var jobs = wanted.map(function (piece) {
                var pack = PIECE_PACK[piece];
                var def = man && man.kits && man.kits[pack] && man.kits[pack].pieces[piece];
                var layerDefs = def && def.layers;
                if (!layerDefs || !layerDefs.length) return Promise.reject(new Error('no sample for ' + piece));
                // Every velocity layer and round-robin of the pieces this file
                // actually plays. A whole kit is ~150 KB against ~2.4 MB for ONE
                // melodic FluidR3 instrument, so per-piece frugality would be
                // optimising the wrong order of magnitude.
                var grid = layerDefs.map(function () { return []; });
                var subJobs = [];
                layerDefs.forEach(function (files, li) {
                    files.forEach(function (base, ri) {
                        subJobs.push(
                            decode(ctx, BASE + '/audio/' + pack + '/' + base + '.' + fmt)
                                .then(function (buf) { grid[li][ri] = buf; })
                        );
                    });
                });
                return Promise.all(subJobs).then(function () {
                    var solid = grid.map(function (rr) { return rr.filter(Boolean); }).filter(function (rr) { return rr.length; });
                    if (!solid.length) throw new Error('no decoded layers for ' + piece);
                    byPiece[piece] = solid;
                    sampled[piece] = true;
                });
            }).map(function (p, i) {
                // Per-piece fallback: one missing file must not cost the whole kit.
                return p.catch(function () {
                    var piece = wanted[i];
                    var gen = SYNTH[piece];
                    if (gen) { try { byPiece[piece] = [[gen(ctx)]]; } catch (_) { /* leave unmapped */ } }
                });
            });

            return Promise.all(jobs).then(function () {
                var out = {};
                (midiNotes || []).forEach(function (m) {
                    var piece = pieceForNote(m);
                    if (!piece || !byPiece[piece]) return;
                    /* the recorded samples' level trims are the same /virtual-drums plays with */
                    var M = noteMap(), trim = (sampled[piece] && M && M.SAMPLE_TRIM && M.SAMPLE_TRIM[piece]) || 1;
                    out[m] = {
                        layers: byPiece[piece],
                        gain: (PIECE_GAIN[piece] != null ? PIECE_GAIN[piece] : 0.9) * trim,
                        pan: PIECE_PAN[piece] || 0,
                        piece: piece
                    };
                });
                return out;
            });
        });
    }

    global.GmDrumKit = {
        pieceForNote: pieceForNote,
        load: load,
        pickLayer: pickLayer,
        PIECE_GAIN: PIECE_GAIN,
        PIECE_PAN: PIECE_PAN
    };
})(window);
